import { Computer, DarkMode, LightMode } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { settingsSectionBodyInsetSx } from './settingsSectionLayout';

interface ThemeButtonProps {
  onThemeChange: (mode: 'light' | 'dark' | 'system') => void;
  mode: 'light' | 'dark' | 'system' | undefined;
}

const ThemeButton = ({ onThemeChange, mode }: ThemeButtonProps) => {
  const { t } = useTranslation();

  if (!mode) {
    return null;
  }

  return (
    <Box
      role="group"
      aria-label={t('aria.theme_toggle_group', 'Theme toggle')}
      sx={{
        ...settingsSectionBodyInsetSx,
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 2,
        justifyContent: 'center',
      }}
    >
      <Button
        value="light"
        variant={mode === 'light' ? 'contained' : 'outlined'}
        size="large"
        color="primary"
        startIcon={<LightMode />}
        onClick={() => onThemeChange('light')}
        fullWidth
        aria-label={t('theme.light', 'Light theme')}
        aria-pressed={mode === 'light'}
        sx={{ justifyContent: 'flex-start' }}
      >
        <Trans i18nKey="theme.light">Light</Trans>
      </Button>

      <Button
        value="dark"
        variant={mode === 'dark' ? 'contained' : 'outlined'}
        size="large"
        color="primary"
        startIcon={<DarkMode />}
        onClick={() => onThemeChange('dark')}
        fullWidth
        aria-label={t('theme.dark', 'Dark theme')}
        aria-pressed={mode === 'dark'}
        sx={{ justifyContent: 'flex-start' }}
      >
        <Trans i18nKey="theme.dark">Dark</Trans>
      </Button>

      <Button
        value="system"
        variant={mode === 'system' ? 'contained' : 'outlined'}
        size="large"
        color="primary"
        startIcon={<Computer />}
        onClick={() => onThemeChange('system')}
        fullWidth
        aria-label={t('theme.system', 'System theme')}
        aria-pressed={mode === 'system'}
        sx={{ justifyContent: 'flex-start' }}
      >
        <Trans i18nKey="theme.system">System</Trans>
      </Button>
    </Box>
  );
};

export default ThemeButton;
